import { reportUnexpectedError } from '@/util/errorSink';
import { getRuntimeConfig } from '@/util/modelCatalog';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { io } from 'socket.io-client';

export const WS_EVENTS = {
  CONNECTED: 'connected',
  JOB_PENDING: 'job_pending',
  JOB_PROCESSING: 'job_processing',
  JOB_DONE: 'job_done',
  JOB_FAILED: 'job_failed',
  DISCONNECT: 'disconnect',
  CONNECT_ERROR: 'connect_error',
  // Client-synthetic event (never sent by the server): dispatched when a
  // reconnect delivers a `connected` event whose session_id differs from the
  // stored one. In-flight jobs are bound to the previous session, so their
  // job_done/job_failed events will never arrive — subscribers use this as
  // the trigger to reconcile via GET /job/{job_id}. See ISSUE-022.
  SESSION_CHANGED: 'session_changed',
};
export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected';
interface WebSocketEmitter {
  on: (event: string, callback: (data: any) => void) => void;
  off: (event: string, callback: (data: any) => void) => void;
  sessionId: string;
  connectionStatus: ConnectionStatus;
}
export const WebSocketContext = createContext<WebSocketEmitter>(
  {} as WebSocketEmitter
);
interface Listener {
  [eventName: string]: {
    callback: ((data: any) => void)[];
  };
}
export const WebSocketContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [sessionId, setSessionId] = useState<string>('');
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>('connecting');
  // Mirror of sessionId readable synchronously inside socket handlers, so a
  // reconnect can compare the incoming session_id against the stored one
  // without waiting for a React re-render. See ISSUE-022.
  const sessionIdRef = useRef<string>('');
  // Listeners live in a ref so that on/off calls do not trigger re-renders
  // (and do not re-run the socket useEffect). See ISSUE-003.
  const listenersRef = useRef<Listener>({} as Listener);
  const on = useCallback((event: string, callback: (data: any) => void) => {
    const listeners = listenersRef.current;
    listeners[event] = {
      callback: [...(listeners[event]?.callback ?? []), callback],
    };
  }, []);
  const off = useCallback((event: string, callback: (data: any) => void) => {
    const listeners = listenersRef.current;
    listeners[event] = {
      callback: (listeners[event]?.callback ?? []).filter(
        (cb) => cb !== callback
      ),
    };
  }, []);
  const [socketIo, setSocketIo] = useState<any>(null);
  useEffect(() => {
    if (socketIo) return;
    let cancelled = false;
    // ISSUE-026: the socket host comes from the runtime configuration
    // (public/models.json `socketHost`), falling back to the compiled
    // REACT_APP_SOCKET_API_HOST. getRuntimeConfig never rejects and never
    // yields an undefined host, so the connection is simply deferred until a
    // concrete host is known — never opened against undefined.
    getRuntimeConfig().then(({ socketHost }) => {
      if (cancelled) return;
      // ISSUE-029: session resumption. The backend (wingwing
      // `handleConnection`) reads an `x-session-id` handshake value and, on
      // resumption, re-emits `connected` with the SAME session_id and
      // replays unacknowledged job events. Two delivery paths:
      // - `auth` callback: re-evaluated by socket.io on EVERY connection
      //   attempt, so it reads the CURRENT session id from sessionIdRef at
      //   attempt time (never the socket-creation value). On the first
      //   connection of a page load sessionIdRef is '' — nothing is sent.
      // - `extraHeaders` (kept current in onConnect below): browsers cannot
      //   attach custom headers to a raw WebSocket upgrade, so extraHeaders
      //   reach the server only on the HTTP long-polling transport. That is
      //   socket.io's default FIRST transport — do NOT restrict `transports`
      //   to ['websocket'] or the header silently disappears.
      setSocketIo(
        io(socketHost, {
          auth: (cb: (data: Record<string, string>) => void) => {
            const previousSessionId = sessionIdRef.current;
            cb(previousSessionId ? { 'x-session-id': previousSessionId } : {});
          },
        })
      );
    });
    return () => {
      cancelled = true;
    };
  }, [socketIo]);
  useEffect(() => {
    if (!socketIo) return;
    const dispatch = (event: string, data: any) => {
      listenersRef.current[event]?.callback?.forEach((callback) => {
        // ISSUE-057. Every listener is isolated: subscribers are independent
        // of each other, so one that throws must not decide whether the rest
        // are notified. Without this try, a synchronous throw unwound the
        // forEach and every listener registered AFTER the thrower silently
        // never ran for that event — a lost job_done, not just a lost error.
        // The try covers the WHOLE iteration body, not only the call: the
        // guard below READS `result.then`, and a returned object with a
        // throwing `then` getter raises there rather than at the call.
        //
        // Recorded exactly once. This catch is the only reporting route a
        // caught throw can take: it no longer reaches the window `error`
        // listener the same sink installs, because it no longer escapes.
        // (The two routes pass different `context` values, and the sink's
        // duplicate suppression keys on (value, context) — so a double record
        // would show as two records, not be collapsed into one. Measured in
        // WebSocketProvider.test.tsx with the real sink installed.)
        try {
          // ISSUE-040. Listeners are typed `(data: any) => void`, but an
          // `async` function satisfies that type, and its returned promise
          // used to be discarded here — so a rejection inside a listener's
          // `await` continuation became an unhandled rejection with nothing
          // left to say where it came from. This is a NET, not a fix: a
          // listener that can fail should still surface its own failure
          // (ISSUE-039 does that for the two known async listeners, and this
          // net then sees nothing).
          const result = callback(data) as unknown;
          if (
            typeof (result as PromiseLike<unknown> | undefined)?.then ===
            'function'
          ) {
            // Promise.resolve(), not result.catch(): a THENABLE is not
            // necessarily a Promise. `{ then() {} }` satisfies the guard above
            // and has no `.catch`, so calling it threw a TypeError out of
            // dispatch — unwinding this forEach and silently skipping every
            // listener registered after it. The net must never be able to
            // break the dispatch it is protecting. Promise.resolve() also
            // adopts a thenable whose own `then` throws, routing that here
            // too — asynchronously, so it lands on the .catch below and not in
            // the catch above.
            Promise.resolve(result).catch((error) =>
              reportUnexpectedError(error, 'websocket listener')
            );
          }
        } catch (error) {
          reportUnexpectedError(error, 'websocket listener');
        }
      });
    };
    const onConnect = ({ session_id }: any) => {
      const previousSessionId = sessionIdRef.current;
      sessionIdRef.current = session_id;
      // ISSUE-029: keep the polling-transport handshake header current for
      // the NEXT reconnect attempt (the Manager re-reads opts.extraHeaders
      // when the engine re-opens). The `auth` callback at the io() call
      // covers transports where custom headers cannot be attached — see the
      // transport note there.
      socketIo.io.opts.extraHeaders = {
        ...socketIo.io.opts.extraHeaders,
        'x-session-id': session_id,
      };
      setSessionId(session_id);
      setConnectionStatus('connected');
      // A reconnect yields a NEW session_id; in-flight jobs stay bound to the
      // old one, so subscribers must reconcile them. First connection
      // (no previous id) and same-id re-delivery trigger nothing.
      if (previousSessionId && previousSessionId !== session_id) {
        dispatch(WS_EVENTS.SESSION_CHANGED, {
          previous_session_id: previousSessionId,
          session_id,
        });
      }
    };
    const onDisconnect = (reason: any) => {
      setConnectionStatus('disconnected');
      dispatch(WS_EVENTS.DISCONNECT, reason);
    };
    const onConnectError = () => {
      setConnectionStatus('disconnected');
    };
    const onPending = ({ job_id, resource_id }: any) => {
      dispatch(WS_EVENTS.JOB_PENDING, { job_id, resource_id });
    };
    const onProcessing = ({ job_id, resource_id }: any) => {
      dispatch(WS_EVENTS.JOB_PROCESSING, { job_id, resource_id });
    };
    const onComplete = ({ job_id, resource_id }: any) => {
      dispatch(WS_EVENTS.JOB_DONE, { job_id, resource_id });
    };
    const onFailed = ({ job_id, resource_id }: any) => {
      dispatch(WS_EVENTS.JOB_FAILED, { job_id, resource_id });
    };
    socketIo.on(WS_EVENTS.CONNECTED, onConnect);
    socketIo.on(WS_EVENTS.DISCONNECT, onDisconnect);
    socketIo.on(WS_EVENTS.CONNECT_ERROR, onConnectError);
    socketIo.on(WS_EVENTS.JOB_PENDING, onPending);
    socketIo.on(WS_EVENTS.JOB_PROCESSING, onProcessing);
    socketIo.on(WS_EVENTS.JOB_DONE, onComplete);
    socketIo.on(WS_EVENTS.JOB_FAILED, onFailed);
    return () => {
      socketIo.off(WS_EVENTS.CONNECTED, onConnect);
      socketIo.off(WS_EVENTS.DISCONNECT, onDisconnect);
      socketIo.off(WS_EVENTS.CONNECT_ERROR, onConnectError);
      socketIo.off(WS_EVENTS.JOB_PENDING, onPending);
      socketIo.off(WS_EVENTS.JOB_PROCESSING, onProcessing);
      socketIo.off(WS_EVENTS.JOB_DONE, onComplete);
      socketIo.off(WS_EVENTS.JOB_FAILED, onFailed);
    };
  }, [socketIo]);

  return (
    <WebSocketContext.Provider value={{ on, off, sessionId, connectionStatus }}>
      {children}
    </WebSocketContext.Provider>
  );
};
